import { useSocket } from "@/contexts/SocketContext.tsx";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect } from "react";

/**
 * Refetches the calendars list + preferences when the backend finishes syncing a
 * connector's calendar list — e.g. right after the user adds a Google/Outlook
 * connector. Preferences are included because the sync job also updates
 * selectedCalendarIds (primary-calendar auto-select). Mount once at the app level.
 *
 * No throttle: the server emits at most once per calendar-list sync run, unlike
 * the per-event-write `calendar:events_changed`.
 */
export const useCalendarsChangedSocket = () => {
  const queryClient = useQueryClient();
  const { socket } = useSocket();

  const invalidateCalendars = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["calendars"] });
    void queryClient.invalidateQueries({ queryKey: ["preferences"] });
  }, [queryClient]);

  useEffect(() => {
    if (!socket) return;

    // `calendar:calendars_changed` is a one-shot emit with no server-side replay,
    // so it's lost if this client was disconnected — or hadn't yet joined the
    // account room — when a connector sync finished. `socket` flips from null to
    // connected on every (re)connect, re-running this effect, so refetch on
    // connect to catch up on anything missed while offline, then listen live.
    invalidateCalendars();
    socket.on("calendar:calendars_changed", invalidateCalendars);
    return () => {
      socket.off("calendar:calendars_changed", invalidateCalendars);
    };
  }, [socket, invalidateCalendars]);
};
